import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit'; // Import the pencil (edit) icon
import AddIcon from '@mui/icons-material/Add'; // Import the Add icon
import CloseIcon from '@mui/icons-material/Close'; // Import the Close icon
import { DataGrid } from '@mui/x-data-grid'; // Importing MUI DataGrid
import { toast } from 'react-toastify';
import ExternalContactModal from './externalContactModal';

const PreferencesModal = ({ isOpen, onClose, agencies }) => {
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [externalContacts, setExternalContacts] = useState([]);
  const [preferencesData, setPreferencesData] = useState([]); // New state for preferences
  const [notificationSchedule, setNotificationSchedule] = useState(null); // State for notification schedule
  const [loading, setLoading] = useState(true); // State to track loading status
  const [error, setError] = useState(null); // State to handle any errors
  const [refresh, setRefresh] = useState(false); // State to force a refresh
  const [contactModalOpen, setContactModalOpen] = useState(false); // State to manage contact modal visibility
  const [selectedContact, setSelectedContact] = useState(null); // For holding the contact being edited (or null for adding)

  useEffect(() => {
    if (agencies && agencies.length > 0 && !selectedAgency) {
      setSelectedAgency(agencies[0]); // Default to the first agency
    }
  }, [agencies, selectedAgency]);

  useEffect(() => {
    const fetchExternalContacts = async () => {
      if (!selectedAgency) return;
      setError(null);

      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
      try {
        const response = await fetch(`${baseURL}user/${selectedAgency.agencyId}/externalcontacts`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching external contacts: ${response.statusText}`);
        }

        const data = await response.json();

        // Sort so active (isActive=true) contacts are at the top, inactive at the bottom
        data.sort((a, b) => {
            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;
            return 0;
          });
        setExternalContacts(data); // Limit to 3 contacts
        setLoading(false);
      } catch (error) {
        console.error('Error fetching external contacts:', error);
        setError('Failed to load external contacts');
        setLoading(false);
      }
    };

    const fetchPreferences = async () => {
      if (!selectedAgency) return;

      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
      try {
        const response = await fetch(`${baseURL}user/${selectedAgency.agencyId}/preferences`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching preferences: ${response.statusText}`);
        }

        const data = await response.json();
        setPreferencesData(data); // Set preferences data
      } catch (error) {
        console.error('Error fetching preferences:', error);
        setError('Failed to load preferences');
      }
    };

    const fetchNotificationSchedule = async () => {
      if (!selectedAgency) return;

      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
      try {
        const response = await fetch(`${baseURL}user/${selectedAgency.agencyId}/notificationschedule`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching notification schedule: ${response.statusText}`);
        }

        const data = await response.json();
        setNotificationSchedule(data); // Set notification schedule data
      } catch (error) {
        console.error('Error fetching notification schedule:', error);
        setError('Failed to load notification schedule');
      }
    };

    if (isOpen) {
      fetchExternalContacts();
      fetchPreferences();
      fetchNotificationSchedule(); // Fetch notification schedule when the modal opens
    }
  }, [isOpen, selectedAgency, refresh]);

  const handleAgencySelect = (agency) => {
    setSelectedAgency(agency);
  };

  const handleAddClick = () => {
    setSelectedContact(null); // Reset selected contact for adding a new one
    setContactModalOpen(true); // Open the modal
  };

  const handleEditClick = (contact) => {
    setSelectedContact(contact); // Set the contact to be edited
    setContactModalOpen(true); // Open the modal
  };

  const handleSubmitContact = async (contact) => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    const method = 'POST'; 

    try {
      const response = await fetch(`${baseURL}user/agencycontact/${contact.agentContactId}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contact),
      });

      if (!response.ok) {
        throw new Error('Error saving contact');
      }
      setRefresh(!refresh); // Trigger refresh after submission
      setContactModalOpen(false); // Close the modal
    } catch (error) {
      console.error('Error saving contact:', error);
    }
  };

  // Function to format phone numbers
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  // Function to call the backend and update the preference
  const updatePreferenceInBackend = async (preferenceId, newStatus) => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    try {
      const response = await fetch(`${baseURL}user/preferences/${preferenceId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isPreferenceActive: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Error updating preference');
      }
    } catch (error) {
      console.error('Failed to update preference:', error);
      throw error; // Rethrow to handle in the toggle function
    }
  };

  // Function to handle toggling preference switches
  const handleTogglePreference = async (id) => {
    try {
      const updatedPreferences = preferencesData.map((preference) => {
        if (preference.transferBondPreferenceId === id) {
          const newStatus = !preference.isPreferenceActive;
          return { ...preference, isPreferenceActive: newStatus };
        }
        return preference;
      });
      setPreferencesData(updatedPreferences);

      const toggledPreference = updatedPreferences.find((p) => p.transferBondPreferenceId === id);
      await updatePreferenceInBackend(id, toggledPreference.isPreferenceActive);

      // Show success toast after successful PATCH request
      toast.success(`Updated ${toggledPreference.preferenceType}`);
    } catch (error) {
      console.error('Failed to toggle preference:', error);
      setError('Failed to update preference. Please try again.');
      setPreferencesData((prevData) =>
        prevData.map((preference) =>
          preference.transferBondPreferenceId === id
            ? { ...preference, isPreferenceActive: !preference.isPreferenceActive } // Revert toggle
            : preference
        )
      );
    }
  };

  // Function to call the PATCH endpoint for updating notification schedule
  const updateNotificationScheduleInBackend = async (day, isActive) => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;

    try {
      const response = await fetch(
        `${baseURL}user/notificationschedule/${notificationSchedule.notificationTimeId}/day/${day}/active/${isActive}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error updating notification schedule');
      }
    } catch (error) {
      console.error('Failed to update notification schedule:', error);
      throw error; // Rethrow to handle in the UI
    }
  };

  // Function to toggle notification days and call the PATCH request
  const handleNotificationToggle = async (day, e) => {
    try {
      await updateNotificationScheduleInBackend(day, e.target.checked);
      setRefresh(!refresh); // Force a refresh to update the UI
    } catch (error) {
      setError('Failed to update notification schedule. Please try again.');
      toast.error(`Unable to update notification schedule`);
      setRefresh(!refresh); // Force a refresh to update the UI
    }
  };

  // Function to update start time and end time
  const handleTimeChange = async (start, end, day) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
      try {
        const response = await fetch(
          `${baseURL}user/notificationschedule/${notificationSchedule.notificationTimeId}/start/${start}/end/${end}/day/${day}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Error updating notification schedule');
        }
      } catch (error) {
        console.error('Failed to update notification schedule:', error);
        throw error; // Rethrow to handle in the UI
      }
      toast.success(`Updated Hours of Operation`);
    } catch (error) {
      setError('Failed to update notification schedule. Please try again.');
      toast.error(`Unable to update notification schedule`);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800, // Keep the width as you prefer
          maxHeight: '80vh', // Set maxHeight to 90% of the viewport height
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          overflowY: 'auto', // Enable scrolling if content exceeds the max height
        }}
      >
        {/* Header with Background Color */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            backgroundColor: '#f9f9f9', // Set the background color for the header
            p: 2, // Add padding for better spacing
            borderRadius: '8px', // Optional: make the edges rounded
          }}
        >
          <Typography variant="h5" sx={{ flexGrow: 1, textAlign: 'center' }}>
            Contacts & Preferences
          </Typography>

          {/* Close Button */}
          <IconButton onClick={onClose} sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Agency Selection Dropdown */}
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            <Autocomplete
              options={agencies || []}
              getOptionLabel={(option) => `${option.dbaAgencyName} (${option.agencyId})`}
              value={selectedAgency}
              onChange={(_, newValue) => handleAgencySelect(newValue)}
              renderInput={(params) => <TextField {...params} label="Modify Agency" fullWidth />}
              sx={{ mb: 4 }}
              disableClearable // This removes the "X" button from the dropdown
            />
          </Grid>
        </Grid>

        {/* Section 1: Preferences */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Notification Preferences
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
          • All notifications will be sent to the phone number(s) and email(s) listed as active in the external contacts.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
          • Preferred Counties means you will only get notifications for bonds in territories you prefer. You can set these in the posting section
        </Typography>

        {/* Display Preferences as Switches */}
        {preferencesData.length > 0 ? (
          <Grid container spacing={2}>
            {preferencesData.map((preference) => (
              <Grid item xs={12} md={4} key={preference.transferBondPreferenceId}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preference.isPreferenceActive}
                      onChange={() => handleTogglePreference(preference.transferBondPreferenceId)}
                      name={preference.preferenceType}
                      color="primary"
                    />
                  }
                  label={preference.preferenceType}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <CircularProgress />
        )}

        {/* Section 2: Notification Schedule */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Notification Schedule
        </Typography>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationSchedule?.anytime ?? false}
                onChange={(e) => handleNotificationToggle(0, e)}
              />
            }
            label="Anytime"
          />
        </Grid>
        <Typography variant="body2" sx={{ mb: 0, pl: 2 }}>
          • Handles when you get notified. If "Anytime" is selected, your contacts will be notified 24/7. Otherwise, please provide your preferred days and hours below.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {(notificationSchedule && (!notificationSchedule?.anytime ?? false)) && (
            <>
            {/* Monday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isMonday ?? false}
                    onChange={(e) => handleNotificationToggle(1, e)}
                  />
                }
                label="Monday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.mondayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    mondayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.mondayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    mondayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.mondayStartTime, notificationSchedule?.mondayEndTime, 0)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          
            {/* Tuesday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isTuesday ?? false}
                    onChange={(e) => handleNotificationToggle(2, e)}
                  />
                }
                label="Tuesday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.tuesdayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    tuesdayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.tuesdayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    tuesdayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.tuesdayStartTime, notificationSchedule?.tuesdayEndTime, 1)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          
            {/* Wednesday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isWednesday ?? false}
                    onChange={(e) => handleNotificationToggle(3, e)}
                  />
                }
                label="Wednesday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.wednesdayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    wednesdayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.wednesdayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    wednesdayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.wednesdayStartTime, notificationSchedule?.wednesdayEndTime, 2)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          
            {/* Thursday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isThursday ?? false}
                    onChange={(e) => handleNotificationToggle(4, e)}
                  />
                }
                label="Thursday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.thursdayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    thursdayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.thursdayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    thursdayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.thursdayStartTime, notificationSchedule?.thursdayEndTime, 3)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          
            {/* Friday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isFriday ?? false}
                    onChange={(e) => handleNotificationToggle(5, e)}
                  />
                }
                label="Friday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.fridayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    fridayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.fridayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    fridayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.fridayStartTime, notificationSchedule?.fridayEndTime, 4)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          
            {/* Saturday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isSaturday ?? false}
                    onChange={(e) => handleNotificationToggle(6, e)}
                  />
                }
                label="Saturday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.saturdayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    saturdayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.saturdayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    saturdayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.saturdayStartTime, notificationSchedule?.saturdayEndTime, 5)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          
            {/* Sunday */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSchedule?.isSunday ?? false}
                    onChange={(e) => handleNotificationToggle(7, e)}
                  />
                }
                label="Sunday"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Start Time"
                label="Start Time"
                type="time"
                value={notificationSchedule?.sundayStartTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    sundayStartTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="End Time"
                label="End Time"
                type="time"
                value={notificationSchedule?.sundayEndTime ?? ''}
                onChange={(e) =>
                  setNotificationSchedule((prevState) => ({
                    ...prevState,
                    sundayEndTime: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleTimeChange(notificationSchedule?.sundayStartTime, notificationSchedule?.sundayEndTime, 6)}
                fullWidth
                size="small"
              >
                Set Hours
              </Button>
            </Grid>
          </>
          
          )}
        </Grid>

        {/* Section 3: External Contacts */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          External Contacts
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
          • Maximum of 10 contacts, only active contacts will receive notifications.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
          • You can edit any contact at any time.
        </Typography>

        {/* Add Button - only show if there are less than 10 contacts */}
        {externalContacts.length < 10 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              Add Contact
            </Button>
          </Box>
        )}

        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : (
          <div style={{ height: 200, width: '100%' }}>
            <DataGrid
              rows={externalContacts}
              columns={[
                { field: 'description', headerName: 'Name', width: 150 },
                { field: 'email', headerName: 'Email', width: 180 },
                {
                  field: 'phone',
                  headerName: 'Phone',
                  width: 130,
                  valueGetter: (params) => formatPhoneNumber(params),
                },
                {
                  field: 'isActive',
                  headerName: 'Active',
                  width: 80,
                  valueGetter: (params) => (params ? 'Yes' : 'No'),
                },
                {
                  field: 'canPost',
                  headerName: 'Can Post',
                  width: 90,
                  valueGetter: (params) => (params ? 'Yes' : 'No'),
                },
                {
                  field: 'canRequest',
                  headerName: 'Can Request',
                  width: 110,
                  valueGetter: (params) => (params ? 'Yes' : 'No'),
                },
                {
                  field: 'edit',
                  headerName: 'Edit',
                  width: 80,
                  renderCell: (params) => (
                    <IconButton onClick={() => handleEditClick(params.row)}>
                      <EditIcon />
                    </IconButton>
                  ),
                },
              ]}
              pageSize={3}
              rowsPerPageOptions={[5]}
              hideFooter // Removes the footer
              disableSelectionOnClick // Disables row selection
              disableColumnSelector // Disables column selection
              getRowId={(row) => row.agentContactId} // Use agentContactId as the row key
            />
          </div>
        )}

        {/* ExternalContactModal for Adding/Editing Contacts */}
        <ExternalContactModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          contact={selectedContact}
          onSubmit={handleSubmitContact}
          agencyId={selectedAgency?.agencyId}
        />
      </Box>
    </Modal>
  );
};

export default PreferencesModal;
